/* eslint-disable max-classes-per-file */
/**
 * This module provides common backend error objects that can be used throughout
 * our backend applications and services.
 *
 * Many of these errors extend the correct Apollo server error object allowing
 * them to be treated as the correct class of errors when used in our GraphQL
 * service.
 */

import { ForbiddenError, UserInputError } from "apollo-server-errors";

import { ObjectId } from "mongodb";

// import { SQSRecord } from "aws-lambda";

export class IncorrectPasswordError extends UserInputError {
  constructor(id: ObjectId, type = "User") {
    super(`Incorrect password for ${type} ${id}`);
  }
}

export class MissingEntityError extends UserInputError {
  constructor(name: string, key: ObjectId | string, source = "ID") {
    super(`Could not find ${name} with ${source} ${key}`);
  }
}

export class UnknownRuntimeEnvironmentError extends Error {
  constructor() {
    super(
      `Expected environment variable 'NODE_ENV' to be one of ['production','t1','t2','development','test'] instead of ${process.env.NODE_ENV}`,
    );
  }
}

export class MissingEnvironmentVariableError extends Error {
  constructor(name: string) {
    super(`Expected environment variable '${name}' to be set`);
  }
}

export class ValidationError extends UserInputError {
  constructor(
    path: string,
    value: string,
    expected: string,
    code = "UNKNOWN_VALIDATION_ERROR",
  ) {
    super(
      `${code}: Expected property at '${path}' to be ${expected} but it was '${value}'`,
      {
        code,
        path,
        value,
      },
    );
  }
}

export class IncorrectBookingStatusError extends UserInputError {
  constructor(status: string) {
    super(`Booking status must be ${status}`);
  }
}

export class SmsIntegrationAuthenticationError extends UserInputError {
  constructor(provider: string) {
    super(`Failed to authenticate with ${provider} SMS integration provider`);
  }
}

export class SmsIntegrationAlreadyConnectedError extends UserInputError {
  constructor() {
    super(
      `SMS integration already connected, please disconnect the current integration before trying again`,
    );
  }
}

export class AppointeddSmsIntegrationUnapprovedError extends UserInputError {
  constructor() {
    super("You must request approval before connecting to Appointedd SMS");
  }
}

export class AppointeddSmsIntegrationAlreadyApprovedError extends UserInputError {
  constructor() {
    super("Appointedd SMS integration has already been approved");
  }
}

export class AppointeddSmsIntegrationAwaitingApprovalError extends UserInputError {
  constructor() {
    super(
      "Appointedd SMS integration has already been requested and is awaiting approval",
    );
  }
}

export class NoCurrentSmsIntegrationError extends UserInputError {
  constructor() {
    super("No current SMS integration");
  }
}

export class SendTestSmsError extends Error {
  constructor() {
    super("Something went wrong when trying to send the SMS message");
  }
}

export class SamlDomainUserEmailMismatchError extends Error {
  constructor(email: string, domain: string) {
    super(
      `Email: ${email} is not a valid email address for SamlDomain: ${domain}`,
    );
  }
}

export class NoStripeIntegrationError extends Error {
  constructor() {
    super("Stripe is not a connected payment provider for this organisation");
  }
}

export class UnprocessableEntityHttpError extends Error {}

export class NotFoundHttpError extends Error {}

export class UnauthorizedError extends Error {}

export class BadRequestError extends Error {}

/**
 * Base class for errors that can be retried.
 */
export class RetryError extends Error {}

/**
 * Webhook sender errors
 */

export class WebhookRetryError extends RetryError {
  readonly response;

  constructor(message: string, response: unknown) {
    super(message);
    this.response = response;
  }
}

export class WebhookNoRetryError extends Error {
  readonly response;

  constructor(message: string, response: unknown) {
    super(message);
    this.response = response;
  }
}

export class BlacklistedSlugError extends UserInputError {
  constructor(slug: string) {
    super(`Cannot use blacklisted slug '${slug}'`, {
      appointeddErrorCode: "BLACKLISTED_SLUG",
    });
  }
}

export class RequiresAdminRoleError extends ForbiddenError {
  constructor(userId: ObjectId, organisationId: ObjectId) {
    super(
      `User: '${userId}' is not an admin on organisation: '${organisationId}'`,
    );
  }
}

export class MissingFeatureFlagError extends ForbiddenError {
  constructor(flagId: string, organisationId: ObjectId) {
    super(
      `Feature flag: '${flagId}' is not enabled on organisation: '${organisationId}' but required for this action.`,
    );
  }
}

export class NonUserCredentialError extends ForbiddenError {
  constructor() {
    super("Request made with a non user credential");
  }
}

export class SamlEnabledOrganisationCreationError extends UserInputError {
  constructor() {
    super(
      "The current organisation is SAML SSO enabled, you must use either `addNewSamlAccount` or `cloneSamlAccount`.",
      { appointeddErrorCode: "SAML_ENABLED_ORG" },
    );
  }
}

export class NonSamlEnabledOrganisationCreationError extends UserInputError {
  constructor() {
    super(
      "The current organisation is not SAML SSO enabled, you must use either `addNewAccount` or `cloneAccount`.",
      { appointeddErrorCode: "NON_SAML_ENABLED_ORG" },
    );
  }
}

export class RequireSamlDomainSuperUserError extends ForbiddenError {
  constructor() {
    super("Request does not have admin permissions for this SamlDomain entity");
  }
}

export class UnsupportedFileFormatError extends UserInputError {
  constructor(fileExtension: string) {
    super(`${fileExtension} is not a supported file format.`);
  }
}

export class UnsupportedScheduleSubjectEntity extends UserInputError {
  constructor(subjectEntity: string) {
    super(`${subjectEntity} is not a supported schedule subject entity type.`);
  }
}

export class ReservationExpiredError extends UserInputError {
  constructor(id: ObjectId, expiry: Date) {
    super(`Reservation with id ${id} expired at ${expiry}.`);
  }
}

export class BccEmailAddressAlreadyExistsError extends UserInputError {
  constructor(address: string, organisationId: ObjectId) {
    super(
      `BCC address '${address}' already exists on organisation '${organisationId}'.`,
    );
  }
}

export class BccEmailAddressAlreadyApprovedError extends UserInputError {
  constructor(address: string, organisationId: ObjectId) {
    super(
      `BCC address '${address}' has already been approved on organisation '${organisationId}'.`,
    );
  }
}

export class InvalidEmailAddressError extends UserInputError {
  constructor(address: string) {
    super(`'${address}' is not a valid email address.`);
  }
}
export class CardPaymentError extends UserInputError {
  constructor(message: string, responseCode: number) {
    super(
      `Card declined with response code ${responseCode} & error: ${message}.`,
      { appointeddErrorCode: "CARD_PAYMENT_ERROR" },
    );
  }
}

export { UserInputError };
